import { BBox, Vec2, angleInArc } from './math';
import { textSpecBBox } from './text';
import { dimensionGeometry } from './dimension';
import { textEntitySpec } from './textspec';
import { flattenSpline } from './spline';
import type { Entity, TextEntity } from './types';

export function textBBox(entity: TextEntity): BBox {
  return textSpecBBox(textEntitySpec(entity));
}

export function entityBBox(entity: Entity): BBox {
  switch (entity.type) {
    case 'line':
      return BBox.fromPoints([entity.a, entity.b]);
    case 'polyline':
      return BBox.fromPoints(entity.vertices);
    case 'circle':
      return new BBox(
        entity.center.x - entity.radius,
        entity.center.y - entity.radius,
        entity.center.x + entity.radius,
        entity.center.y + entity.radius,
      );
    case 'arc':
      return arcBBox(entity.center, entity.radius, entity.startAngle, entity.endAngle, entity.ccw);
    case 'ellipse': {
      const c = Math.cos(entity.rotation);
      const s = Math.sin(entity.rotation);
      const hx = Math.hypot(entity.rx * c, entity.ry * s);
      const hy = Math.hypot(entity.rx * s, entity.ry * c);
      return new BBox(
        entity.center.x - hx,
        entity.center.y - hy,
        entity.center.x + hx,
        entity.center.y + hy,
      );
    }
    case 'spline':
      return BBox.fromPoints(flattenSpline(entity));
    case 'text':
      return textBBox(entity);    case 'hatch':      return BBox.fromPoints(entity.loops.flat());
    case 'dimension': {
      const geometry = dimensionGeometry(entity);
      const points = [...geometry.lines.flat(), ...geometry.arrows.flat()];
      const box = points.length > 0 ? BBox.fromPoints(points) : BBox.empty();
      return box.union(textSpecBBox(geometry.text));
    }
    default:
      return BBox.empty();
  }
}

export function arcBBox(
  center: Vec2,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
): BBox {
  const candidates = [
    Vec2.polar(radius, startAngle),
    Vec2.polar(radius, endAngle),
    Vec2.polar(radius, 0),
    Vec2.polar(radius, Math.PI / 2),
    Vec2.polar(radius, Math.PI),
    Vec2.polar(radius, (3 * Math.PI) / 2),
  ];
  const angles = [startAngle, endAngle, 0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];
  const points: Vec2[] = [];
  for (let i = 0; i < candidates.length; i++) {
    const angle = angles[i]!;
    if (i < 2 || angleInArc(angle, startAngle, endAngle, ccw)) {
      points.push(candidates[i]!.add(center));
    }
  }
  return BBox.fromPoints(points);
}
